import { Injectable, inject } from "@angular/core";
import { SafeUrl } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { Observable, Subject } from "rxjs";
import { LOCATION, WINDOW } from "@tylertech/shared-module";
import { RouteInfoService } from "./routeInfo.service";

@Injectable()
export class NavigationService
{
    private readonly window = inject<globalThis.Window>(WINDOW);
    private readonly location = inject<Location>(LOCATION);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly $routeInfoService = inject(RouteInfoService);

    private _isNavigating = false;
    private _isPopStateNavigation = false;

    private resetState$ = new Subject<void>();

    constructor()
    {
        this.router.events.subscribe(event =>
        {
            if (event.type === 0)
                this._isPopStateNavigation = event.navigationTrigger === "popstate";
            else if (event.type === 1)
                this._isPopStateNavigation = false;
        });
    }

    get isNavigating(): boolean { return this._isNavigating; }
    get isPopStateNavigation(): boolean { return this._isPopStateNavigation; }

    /** @deprecated - There does not appear to be any usages of this method. It should be removed. */
    back(): void
    {
        this.window.history.back();
    }

    /**
     * This should only used for navigating to a different app than the current app. If you are wanting to navigate to a
     * different Route within the same app use the Angular Router
    */
    getLinkToApp(appName: string, queryParams?: Record<string, string | number | boolean>): SafeUrl
    {
        appName = appName.indexOf("/apps/") > -1
            ? this.stripApps(appName)
            : appName.indexOf("/") === 0
                ? appName.substring(1)
                : appName;
        if (appName.indexOf(".") > -1)
        {
            appName = appName + "/";
        }

        const url = `/${this.$routeInfoService.tenant}/apps/${appName}`;
        if (!queryParams)
        {
            return url;
        }

        const queryString = Object.keys(queryParams)
            .map(k =>
            {
                return `${k}=${queryParams[k]}`;
            })
            .join("&");

        return `${url}/?${queryString}`;
    }

    /**
     * This should only used for navigating to a different app than the current app. If you are wanting to navigate to a
     * different Route within the same app use the Angular Router
    */
    navigateToApp(appName: string, queryParams?: Record<string, string | number | boolean>): void
    {
        this.location.href = this.getLinkToApp(appName, queryParams) as string;
    }

    onResetState(): Observable<void>
    {
        return this.resetState$.asObservable();
    }

    triggerReset(): void
    {
        this.resetState$.next();
    }

    navigateToUrl(url: string): void
    {
        if (!url)
        {
            return;
        }

        const path = url.replace("//", "/");
        const [pathPart, queryPart] = path.split("?");
        const parts = pathPart.split("/");
        const start = parts.indexOf("apps") + 1;
        const appName = parts[start];

        if (appName === "form" && this.$routeInfoService.appName === "form")
        {
            this._isNavigating = true;

            let commands = parts.slice(start + 1, parts.length)
                .filter(c => c.length > 0)
                .map(c => decodeURIComponent(c));
            commands = commands.length === 0 ? ["/"] : commands;

            const queryParams = queryPart
                ? queryPart.split("&")
                    .reduce((params: Record<string, string>, paramStr) =>
                    {
                        const [name, value] = paramStr.split("=", 2);
                        params[name] = value ? decodeURIComponent(value.replace(/\+/g, "%20")) : "";
                        return params;
                    }, {})
                : undefined;

            this.triggerReset();

            this.router.navigate(
                commands,
                {
                    queryParams: queryParams,
                    relativeTo: this.activatedRoute.root
                })
                .finally(() =>
                {
                    this.$routeInfoService.setPath();
                    setTimeout(() => this._isNavigating = false, 100);
                });

            return;
        }

        let route = parts.slice(start, parts.length).join("/");
        if (queryPart)
            route =  `${route}?${queryPart}`;
        this.navigateToApp(route);
    }

    openApp(appName: string, target: string, queryParams?: Record<string, string | number | boolean>): void
    {
        this.window.open(this.getLinkToApp(appName, queryParams) as string, target);
    }

    redirectToDashboard(): void
    {
        this.navigateToApp("dashboard");
    }

    redirectRoutingForVendor(): void
    {
        if (!this.isApp("vendormanager"))
        {
            this.navigateToApp("vendormanager");
        }
    }

    private stripApps(appNameWithApps: string): string
    {
        return appNameWithApps.replace("/apps/", "");
    }

    isApp(appName: string, internalRoutes?: ReadonlyArray<string>): boolean
    {
        if (this.$routeInfoService.appName !== appName)
        {
            return false;
        }

        if (!internalRoutes
            || internalRoutes.length === 0)
        {
            return true;
        }

        return this.activatedRoute.snapshot.url.reduce(
            (valid, urlSegment, index) =>
            {
                return valid && urlSegment.path === internalRoutes[index];
            },
            true);
    }
}
